/**
 * 알피 고객센터 위젯 연동 (BUNKER KRE-38 · 알피 3단계).
 *
 * 켜는 방법: 빌드 환경변수 VUE_APP_ALFIE_CHAT_ORIGIN 에 위젯 출처(https://alfie-chat-dev.naezipscan.com 등)를 넣는다.
 * Vercel 운영 빌드는 vue.config.js 가 운영 위젯(https://alfie-chat.naezipscan.com)을 넣는다(2026-10-28).
 * 비어 있으면 꺼짐 — 채널톡이 지금과 똑같이 뜨고 이 파일의 어떤 코드도 화면을 바꾸지 않는다.
 *
 * 켜지면:
 *  - 채널톡 스크립트를 싣지 않고 window.ChannelIO 를 알피로 넘기는 대역(shim)으로 바꾼다(ChannelService.loadScript).
 *    기존 채널톡 호출(boot·showMessenger·openChat·show/hideChannelButton …)이 고칠 곳 없이 알피로 간다.
 *  - naezipscan.channel.io 로 가는 새 창·링크(약 40곳)를 문서 단위로 가로채 알피 위젯을 연다.
 *  - 위젯 스크립트를 못 실으면 채널톡 상담 페이지 새 창으로 돌아간다(문의 통로가 막히지 않게).
 *  - 오픈 전 내부 테스트(알피 인입 `internal`)에서 명단 밖 고객은 위젯이 `intake_closed` 를 알려 준다 → 그 고객에게는
 *    진짜 채널톡을 지금처럼 띄운다(channelTalk.start). 명단에 있는 테스터가 로그인하면 `ready` → 채널톡을 내리고 알피로 바꾼다.
 *    판정 전 오류·위젯 로딩 실패·판정 지연(10초)도 채널톡으로 간다(문의 통로가 비지 않게). 채널톡을 해지하면 호출부에서 channelTalk 을 뺀다.
 * 로그인 연결 링크 조각(#alfie=…&t=…)은 위젯 설치 스크립트가 직접 읽고 지운다 — 여기서 건드리지 않는다(두 번 처리하면 경합).
 *
 * 켜도 지금처럼 채널톡을 쓰는 곳: 자산원클릭 도메인, 리포트 샘플(원래 미노출).
 * 다방 인앱 세션도 알피(노션 정본 '다방 유입은 리포트로 고객 매칭', 오너 2026-10-25 — 계약 조건 없음). 진입 컨텍스트 kind 'dabang' +
 * 리포트 코드, 테스터 규칙은 다른 화면과 같다(명단 밖이면 다방 전용 채널톡 흐름 그대로 — views/DabangSupport.vue).
 * web-core(src/lib/alfie/alfieWidget.ts)와 같은 규칙이다 — 바꿀 때 양쪽을 같이 고친다.
 * 다만 다방 문의 페이지 전용(미룬 부팅·판정 기다리기·창 열림 듣기)은 Vue 에만 있다 — 다방 문의 페이지가 Vue 에만 있기 때문.
 */

export const CHANNEL_TALK_HOST = "naezipscan.channel.io";
export const CHANNEL_TALK_URL = "https://naezipscan.channel.io";

const ORIGIN_PATTERN = /^https:\/\/[a-z0-9.-]+(:\d+)?$|^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** 위젯 스크립트가 이 시간 안에 안 뜨면 그동안 누른 문의는 채널톡 상담 페이지로 연다(늦게 뜨면 위젯은 그대로 뜬다) */
const READY_TIMEOUT_MS = 8000;
/** 채널톡 연결이 있을 때: 위젯이 실린 뒤 이 시간 안에 세션 판정(ready·intake_closed)이 안 오면 채널톡을 띄운다(나중에 ready 가 오면 알피로) */
const VERDICT_TIMEOUT_MS = 10000;

/** 위젯 출처(꺼짐이면 null). 형식이 틀리면 꺼진 것으로 본다 */
export function alfieOrigin(raw = process.env.VUE_APP_ALFIE_CHAT_ORIGIN) {
  if (typeof raw !== "string") return null;
  const origin = raw.trim().replace(/\/+$/, "");
  return ORIGIN_PATTERN.test(origin) ? origin : null;
}

/** 이 화면에서 알피를 쓰는가(꺼짐·예외 화면이면 false — 채널톡 그대로) */
export function alfieEnabledFor({ origin, hostname, pathname }) {
  if (origin === null) return false;
  if (hostname === "jasanoneclick.com" || hostname.endsWith(".jasanoneclick.com")) return false;
  if (pathname.startsWith("/report/sample")) return false;
  return true;
}

function currentWindow() {
  return typeof window === "undefined" ? null : window;
}

/** 다방 인앱 세션인가(다방 문의 페이지로 들어왔거나 그 탭에서 이어 보는 중) — 진입 컨텍스트 kind 'dabang' */
export function isDabangSession(w = currentWindow()) {
  if (w === null) return false;
  try {
    return (
      w.location.pathname.startsWith("/dabang") ||
      w.sessionStorage.getItem("dabang_entered") === "1"
    );
  } catch (e) {
    return false;
  }
}

let errorScreen = null;

/** 오류 화면 참조번호(화면이 정한 뒤 알려 준다) — 그 화면에서 문의하면 담당자에게 같이 보인다(노션 진입 컨텍스트) */
export function setAlfieErrorRef(ref, path) {
  errorScreen = { ref, path };
}

/** 주소 속 토큰·긴 식별자는 담당자 화면·AI 프롬프트로 보내지 않는다(로그인 없이 토큰으로 여는 화면들) */
export function maskEntryPath(pathname) {
  return pathname
    .split("/")
    .map(segment => (/^[A-Za-z0-9_-]{20,}$/.test(segment) ? ":token" : segment))
    .join("/")
    .slice(0, 200);
}

/** 진입 컨텍스트(유저 화면에는 안 보이고 담당자에게만). 다방 세션(entryKind)은 어느 화면이든 다방. web-core alfieEntryFor 와 같은 규칙 */
export function alfieEntryFor({ entryKind, app, pathname, errorScreen: screen }) {
  const onErrorScreen = Boolean(screen) && screen.path === pathname;
  const kind = entryKind || (onErrorScreen ? "error_screen" : app ? "app_floating" : "web_floating");
  return { kind, path: maskEntryPath(pathname), ...(onErrorScreen ? { errorRef: screen.ref } : {}) };
}

/** 지금 화면에서 알피를 쓰는가 */
export function isAlfieEnabled() {
  const w = currentWindow();
  if (w === null) return false;
  return alfieEnabledFor({
    origin: alfieOrigin(),
    hostname: w.location.hostname,
    pathname: w.location.pathname,
  });
}

/** 채널톡 상담 주소인가(새 창·링크 가로채기 판정) */
export function isChannelTalkUrl(url, base) {
  if (typeof url !== "string" || url === "") return false;
  try {
    return new URL(url, base).hostname === CHANNEL_TALK_HOST;
  } catch (e) {
    return false;
  }
}

export const ALFIE_PUSH_ACTION = "alfie_reply";

/** 앱 푸시 탭(push_action alfie_reply) → 열 요청(상담 ID가 이상하면 고객센터 첫 화면). 아니면 null */
export function alfiePushRequest(payload) {
  if (!payload || payload.push_action !== ALFIE_PUSH_ACTION) return null;
  const conversationId = String(payload.conversation_id || "");
  return UUID_PATTERN.test(conversationId) ? { conversationId } : {};
}

/**
 * 채널톡 명령 → 알피 동작. 상담을 여는 명령은 open, 버튼 숨김/보임은 launcher, 나머지(boot·updateUser·track·콜백 등록)는 무시.
 */
export function channelCommandAction(command) {
  switch (command) {
    case "showMessenger":
    case "openChat":
    case "openWorkflow":
      return "open";
    case "hideChannelButton":
      return "hide";
    case "showChannelButton":
      return "show";
    default:
      return "ignore";
  }
}

// ---- 화면 연동(브라우저 전용) ----

let booted = false;
let ready = false;
let failed = false;
let slow = false;
let pending = [];
let launcherHidden = false;
let launcherBottom = 0;
let originalOpen = null;
/** 세션 판정 전(waiting) · 알피(alfie) · 채널톡(channel — 오픈 전 내부 테스트 명단 밖) */
let mode = "waiting";
let channelTalk = null;
/** 채널톡에서 알피로 바꿀 때 치워 둔 진짜 ChannelIO(다시 채널톡으로 갈 때 되돌린다) */
let realChannelIO;
/** 판정 전에 고객센터를 눌렀다(채널톡으로 바뀌면 메신저를 연다) */
let openedWhileWaiting = false;
/** 채널톡 대체가 사용자의 문의를 대신 열었다(늦게 알피로 바뀌면 알피에서 다시 연다, 리뷰 m5) */
let openedByFallback = false;
/** 판정(알피·채널톡)을 기다리는 쪽(다방 문의 페이지) */
let decisionWaiters = [];
/** 미뤄 둔 부팅(다방 문의 페이지 — 토큰 교환 뒤 releaseAlfieBoot) */
let deferredBoot = null;
let releaseRequested = false;
/** 상담 창 열림·닫힘을 듣는 쪽(다방 문의 페이지 — 닫히면 '상담창이 닫혔습니다') */
const visibilityListeners = new Set();

function resolveDecision() {
  if (mode === "waiting") return;
  const waiters = decisionWaiters;
  decisionWaiters = [];
  for (const waiter of waiters) waiter(mode);
}

/** 이 사람에게 채널톡을 띄운다(오픈 전 내부 테스트 명단 밖). 판정 전에 누른 고객센터는 채널톡으로 연다 */
function switchToChannelTalk(w, openNow) {
  if (!channelTalk) return;
  if (mode === "channel") {
    resolveDecision();
    return;
  }
  mode = "channel";
  const open = openNow || openedWhileWaiting || pending.length > 0;
  openedWhileWaiting = false;
  pending = [];
  if (w.ChannelIO === channelShim) w.ChannelIO = realChannelIO;
  channelTalk.start();
  if (launcherHidden && w.ChannelIO) w.ChannelIO("hideChannelButton");
  if (open && w.ChannelIO) w.ChannelIO("showMessenger");
  openedByFallback = open;
  resolveDecision();
}

/** 알피를 쓴다(명단에 있는 테스터·오픈 뒤 전원). 채널톡이 떠 있었으면 내린다 */
function switchToAlfie(w) {
  // 판정 전에 누른 문의는 위젯이 쌓아 둔 요청으로 알피에서 열린다 — 나중 채널톡 전환 때 다시 열지 않게 지운다
  openedWhileWaiting = false;
  if (mode !== "alfie") {
    const wasChannel = mode === "channel";
    mode = "alfie";
    if (wasChannel && channelTalk) {
      channelTalk.stop();
      realChannelIO = w.ChannelIO;
      w.ChannelIO = channelShim;
      // 채널톡이 대신 열어 줬던 문의는 알피에서 이어 연다(사용자가 누른 상담이 증발하지 않게)
      if (openedByFallback && ready && w.AlfieWidget) w.AlfieWidget.open({});
    }
  }
  openedByFallback = false;
  resolveDecision();
}

/** 위젯이 알려 준 세션 결과. 오류는 판정 전일 때만 채널톡(이미 알피·채널톡이면 그대로 — 일시 오류로 바꾸지 않는다) */
export function handleAlfieSession(state, detail) {
  const w = currentWindow();
  if (w === null) return;
  if (state === "ready") switchToAlfie(w);
  else if (state === "intake_closed" || mode === "waiting")
    switchToChannelTalk(w, !!(detail && detail.pendingOpen));
  // 판정을 기다리는 쪽에는 오류 때도 지금 상태를 알려 준다(알피·채널톡 중 하나로 이미 정해졌으면)
  resolveDecision();
}

function fallbackToChannelTalk() {
  const w = currentWindow();
  if (w === null) return;
  // 앱 웹뷰의 새 창은 iOS에서 앱이 닫힌다(safeOpenUrl) — 같은 창으로
  if (w.flutter_inappwebview) w.location.href = CHANNEL_TALK_URL;
  else (originalOpen || w.open).call(w, CHANNEL_TALK_URL, "_blank");
}

/** 알피 위젯을 연다(아직 안 떴으면 뜬 뒤에). 위젯을 못 실었으면 채널톡 상담 페이지 */
export function openAlfie(request = {}) {
  const w = currentWindow();
  if (w === null) return;
  if (mode === "channel") {
    if (w.ChannelIO) w.ChannelIO("showMessenger");
    return;
  }
  if (mode === "waiting") openedWhileWaiting = true;
  if (ready && w.AlfieWidget) {
    w.AlfieWidget.open(request);
    return;
  }
  if (failed || slow) {
    fallbackToChannelTalk();
    return;
  }
  pending.push(request);
}

/**
 * 판정('alfie' | 'channel')을 기다린다 — 다방 문의 페이지가 세션 교환·위젯 부팅 뒤 부른다.
 * 이미 정해졌으면(위젯 장애로 채널톡 등) 바로, 아니면 다음 판정에서, 판정이 안 오면 timeoutMs 뒤 지금 상태로.
 */
export function waitAlfieDecision(timeoutMs = 12000) {
  const w = currentWindow();
  if (w === null) return Promise.resolve("channel");
  if (mode !== "waiting") return Promise.resolve(mode);
  return new Promise(resolve => {
    let done = false;
    const finish = result => {
      if (done) return;
      done = true;
      resolve(result);
    };
    decisionWaiters.push(finish);
    w.setTimeout(() => {
      // 판정이 끝내 안 오면 실제로 채널톡으로 바꾼 뒤 답한다(대역만 둔 채 'channel' 이라 답하면 다방 페이지가 알피 창과 채널톡을 함께 연다, 리뷰 M1)
      if (mode === "waiting" && channelTalk) switchToChannelTalk(w, false);
      finish(mode === "alfie" ? "alfie" : "channel");
    }, timeoutMs);
  });
}

/**
 * 미뤄 둔 부팅을 푼다 — 다방 문의 페이지는 다방 토큰을 로그인으로 바꾼 뒤에 위젯을 띄워야 첫 판정이 그 신원으로 나온다
 * (먼저 띄우면 비로그인 판정이 늦게 와 테스터를 채널톡으로 보낼 수 있다, 리뷰 W1). App 이 아직 부팅을 부르지 않았으면 부를 때 바로 띄운다.
 */
export function releaseAlfieBoot() {
  releaseRequested = true;
  if (deferredBoot === null) return false;
  const options = deferredBoot;
  deferredBoot = null;
  return bootAlfie(options);
}

/** 상담 창 열림·닫힘 듣기(해제 함수를 돌려준다) */
export function onAlfieVisibility(listener) {
  visibilityListeners.add(listener);
  return () => visibilityListeners.delete(listener);
}

/** 하단 고정 영역 높이만큼 플로팅 버튼을 올린다(위젯이 뜨기 전이면 뜰 때 적용) */
export function setAlfieLauncherOffset(bottom) {
  const w = currentWindow();
  launcherBottom = Number.isFinite(bottom) && bottom > 0 ? bottom : 0;
  if (ready && w && w.AlfieWidget) w.AlfieWidget.setLauncherOffset({ bottom: launcherBottom });
}

/** 로그인·로그아웃 뒤 신원을 다시 확인하게 한다(비로그인 상담은 계정으로 옮겨진다) */
export function refreshAlfieIdentity() {
  const w = currentWindow();
  if (ready && w && w.AlfieWidget) w.AlfieWidget.refreshIdentity();
}

function applyLauncher(w) {
  if (!ready || !w.AlfieWidget) return;
  if (launcherHidden) w.AlfieWidget.hideLauncher();
  else w.AlfieWidget.showLauncher();
}

/** window.ChannelIO 대역 — ChannelService 가 채널톡 스크립트 대신 이것을 건다 */
export function channelShim(command) {
  const w = currentWindow();
  if (w === null) return;
  const action = channelCommandAction(command);
  if (action === "open") openAlfie({});
  else if (action === "hide" || action === "show") {
    launcherHidden = action === "hide";
    applyLauncher(w);
  }
}

function interceptChannelTalkLinks(w) {
  originalOpen = w.open;
  w.open = function alfieAwareOpen(url, ...rest) {
    // 채널톡을 쓰는 고객(오픈 전 명단 밖)은 원래대로 채널톡 상담 페이지
    if (mode !== "channel" && isChannelTalkUrl(String(url || ""), w.location.href)) {
      openAlfie({});
      return null;
    }
    return originalOpen.call(w, url, ...rest);
  };
  w.document.addEventListener(
    "click",
    event => {
      const target = event.target;
      const anchor =
        target && typeof target.closest === "function" ? target.closest("a[href]") : null;
      if (
        mode === "channel" ||
        anchor === null ||
        !isChannelTalkUrl(anchor.getAttribute("href"), w.location.href)
      )
        return;
      event.preventDefault();
      openAlfie({});
    },
    true,
  );
}

/**
 * 전역 셸(App.vue)에서 한 번. options: { getIdentityToken, app, onLoginRequest, notifications, channelTalk: { start, stop }, entryKind?, reportSeq?, defer? }
 * defer: 다방 문의 페이지 — releaseAlfieBoot() 를 부를 때까지 띄우지 않는다.
 * 이미 부팅했거나 이 화면에서 꺼져 있으면 아무것도 하지 않는다.
 */
export function bootAlfie(options) {
  const w = currentWindow();
  const origin = alfieOrigin();
  if (w === null || booted || !isAlfieEnabled()) return false;
  if (options.defer && !releaseRequested) {
    deferredBoot = { ...options, defer: false };
    return true;
  }
  booted = true;
  channelTalk = options.channelTalk || null;
  interceptChannelTalkLinks(w);
  // 위젯을 못 싣거나 늦으면: 채널톡 연결이 있으면 채널톡을 띄우고, 없으면 누른 문의만 채널톡 상담 페이지로
  const giveUp = () => {
    if (channelTalk) switchToChannelTalk(w, false);
    else if (pending.length > 0) fallbackToChannelTalk();
    pending = [];
  };
  const timer = w.setTimeout(() => {
    if (ready) return;
    slow = true;
    giveUp();
  }, READY_TIMEOUT_MS);
  const script = w.document.createElement("script");
  script.src = `${origin}/alfie-widget.js`;
  script.async = true;
  script.onload = () => {
    w.clearTimeout(timer);
    if (!w.AlfieWidget) {
      failed = true;
      giveUp();
      return;
    }
    w.AlfieWidget.boot({
      chatOrigin: origin,
      getIdentityToken: options.getIdentityToken,
      entry: {
        ...alfieEntryFor({ entryKind: options.entryKind, app: options.app, pathname: w.location.pathname, errorScreen }),
        ...(options.reportSeq ? { reportSeq: String(options.reportSeq).slice(0, 60) } : {}),
      },
      // 열 때마다 지금 화면 기준으로(부팅 뒤 옮긴 화면·오류 화면 참조번호). 알피 쪽이 아직 모르면 위 entry 그대로
      getEntry: () => alfieEntryFor({ entryKind: options.entryKind, app: options.app, pathname: w.location.pathname, errorScreen }),
      app: options.app,
      onLoginRequest: options.onLoginRequest,
      onSession: handleAlfieSession,
      onVisibility: open => {
        for (const listener of visibilityListeners) {
          try {
            listener(open);
          } catch (e) {
            // 듣는 쪽 오류는 무시
          }
        }
      },
      ...(options.notifications ? { notifications: options.notifications } : {}),
    });
    ready = true;
    if (channelTalk) {
      w.setTimeout(() => {
        if (mode === "waiting") switchToChannelTalk(w, false);
      }, VERDICT_TIMEOUT_MS);
    }
    w.AlfieWidget.setLauncherOffset({ bottom: launcherBottom });
    if (launcherHidden) w.AlfieWidget.hideLauncher();
    const queued = pending;
    pending = [];
    for (const request of queued) w.AlfieWidget.open(request);
  };
  script.onerror = () => {
    w.clearTimeout(timer);
    failed = true;
    giveUp();
  };
  w.document.head.appendChild(script);
  return true;
}

/** 테스트 전용 — 모듈 상태 초기화 */
export function resetAlfieForTest() {
  booted = false;
  ready = false;
  failed = false;
  slow = false;
  pending = [];
  launcherHidden = false;
  launcherBottom = 0;
  originalOpen = null;
  mode = "waiting";
  channelTalk = null;
  realChannelIO = undefined;
  openedWhileWaiting = false;
  openedByFallback = false;
  decisionWaiters = [];
  visibilityListeners.clear();
  deferredBoot = null;
  releaseRequested = false;
}
